import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { ArrowRight, X } from "lucide-react";
import { menu } from "../lib/data";
import { fetchManagedMenu } from "../lib/menuApi";
import { withMenuIds } from "../lib/menuEditor";

// Curated 6 highlights shown on the landing preview.
// Pulled from the "Traditional Greek Mains" + "Gyros & Souvlaki" + "Salads" sections.
const preview = [
  {
    name: "Lamb Kleftiko",
    description:
      "Slow-roasted lamb with potatoes, peppers, mushrooms, gravy, parmesan & feta.",
    tag: "House signature",
  },
  {
    name: "Souvlaki",
    description:
      "Char-grilled skewers with chips, salad, tzatziki & pita. Also available with chicken.",
    tag: "Classic",
  },
  {
    name: "Gyros Plate",
    description:
      "Pork & chicken gyros with hand-cut chips, salad, tzatziki & warm pita.",
    tag: "Crowd favourite",
  },
  {
    name: "Moussaka",
    description: "Layers of aubergine, spiced beef and a golden béchamel. Daily cooked.",
    tag: "Family recipe",
  },
  {
    name: "Beef Stifado",
    description:
      "Rich red-wine stew with pearl onions, cinnamon & bay — slow-simmered for hours.",
    tag: "Slow-cooked",
  },
  {
    name: "Greek Salad",
    description:
      "Tomato, cucumber, onion, pepper, Kalamata olives, oregano & a slab of feta.",
    tag: "Garden fresh",
  },
];

const preferredPreviewOrder = preview.map((item) => item.name.toLowerCase());

const buildPreview = (categories) => {
  const allItems = categories.flatMap((category) =>
    (category.items || [])
      .filter((item) => item.available !== false)
      .map((item) => ({
        ...item,
        tag: item.tag || category.name,
      })),
  );

  const preferred = preferredPreviewOrder
    .map((name) => allItems.find((item) => item.name.toLowerCase().includes(name)))
    .filter(Boolean);

  const merged = [...preferred, ...allItems].filter(
    (item, index, list) => list.findIndex((candidate) => candidate.name === item.name) === index,
  );

  return merged.slice(0, 6).length ? merged.slice(0, 6) : preview;
};

export const Menu = () => {
  const [open, setOpen] = useState(false);
  const [menuCategories, setMenuCategories] = useState(() => withMenuIds(menu));

  useEffect(() => {
    let mounted = true;
    fetchManagedMenu()
      .then((data) => {
        if (mounted && data.categories?.length) {
          setMenuCategories(withMenuIds(data.categories));
        }
      })
      .catch(() => {
        if (mounted) {
          setMenuCategories(withMenuIds(menu));
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const previewItems = useMemo(() => buildPreview(menuCategories), [menuCategories]);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[#f0ebe1] py-24 md:py-32 lg:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 reveal">
            <p className="overline text-[#5b7053]">From the Kitchen</p>
            <h2 className="font-serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#2c3528]">
              A taste of what's{" "}
              <span className="italic text-[#d98a6c]">on the table</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-[#2c3528]/80 text-lg leading-relaxed max-w-md">
              Six house favourites to whet your appetite — the full menu runs
              from meze and slow-roasted lamb to pizza, pasta and a long
              cocktail list.
            </p>
          </div>
        </div>

        {/* Preview grid */}
        <ul
          data-testid="menu-preview-list"
          className="mt-16 md:mt-24 grid md:grid-cols-2 gap-x-12 md:gap-x-16 gap-y-0 divide-y md:divide-y-0 divide-[#2c3528]/15"
        >
          {previewItems.map((d, i) => (
            <li
              key={d.name}
              data-testid={`menu-preview-${i}`}
              className="reveal group py-8 md:py-10 md:border-b md:border-[#2c3528]/15"
            >
              <div className="flex items-baseline gap-4 mb-3">
                <span className="font-serif-display text-[#d98a6c] text-lg tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif-display text-3xl md:text-4xl text-[#2c3528] leading-none group-hover:text-[#5b7053] transition-colors">
                  {d.name}
                </h3>
              </div>
              <p className="text-[#2c3528]/75 leading-relaxed text-base max-w-md">
                {d.description}
              </p>
              <p className="mt-3 overline text-[#5b7053]/80">· {d.tag}</p>
            </li>
          ))}
        </ul>

        {/* CTAs */}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-t border-[#2c3528]/20 pt-10">
          <p className="text-[#2c3528]/75 italic font-serif-display text-xl max-w-xl leading-snug">
            Daily specials change with the catch and the season — ask your
            server, or better, ask Lorenc.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="open-full-menu"
                  className="lift inline-flex items-center gap-3 bg-[#2c3528] hover:bg-[#3f4f39] text-[#f9f6f0] px-7 py-4 text-sm tracking-[0.18em] uppercase"
                >
                  See the Full Menu
                  <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
                </button>
              </DialogTrigger>

              <DialogContent
                data-testid="full-menu-dialog"
                className="max-w-[1100px] w-[96vw] h-[92vh] p-0 bg-[#f9f6f0] border border-[#2c3528]/15 rounded-none overflow-hidden flex flex-col"
              >
                <DialogTitle className="sr-only">The Greek Yum Yum's full menu</DialogTitle>
                <DialogDescription className="sr-only">
                  Browse all public menu sections, dishes and prices.
                </DialogDescription>
                <FullMenu categories={menuCategories} onClose={() => setOpen(false)} />
              </DialogContent>
            </Dialog>

            <a
              href="#visit"
              data-testid="menu-visit-cta"
              className="lift inline-flex items-center gap-3 bg-[#5b7053] hover:bg-[#3f4f39] text-[#f9f6f0] px-7 py-4 text-sm tracking-[0.18em] uppercase"
            >
              Reserve a Table
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------- Full menu dialog content -------- */

const FullMenu = ({ categories, onClose }) => {
  const visibleCategories = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          items: (category.items || []).filter((item) => item.available !== false),
        }))
        .filter((category) => category.items.length),
    [categories],
  );
  const [active, setActive] = useState(visibleCategories[0]?.name || "");

  useEffect(() => {
    setActive(visibleCategories[0]?.name || "");
  }, [visibleCategories]);

  const scrollToCategory = (name) => {
    setActive(name);
    const id = "dlg-cat-" + slug(name);
    const el = document.getElementById(id);
    const scroller = document.getElementById("full-menu-scroll");
    if (el && scroller) {
      const top =
        el.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        16;
      scroller.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 md:px-10 pt-8 pb-5 border-b border-[#2c3528]/15 bg-[#f9f6f0]">
        <div>
          <p className="overline text-[#5b7053]">Full Menu</p>
          <h3 className="font-serif-display text-3xl md:text-4xl text-[#2c3528] mt-2 leading-tight">
            The Greek Yum Yum&rsquo;s
          </h3>
        </div>
        <button
          onClick={onClose}
          data-testid="close-full-menu"
          className="lift mt-2 h-10 w-10 grid place-items-center border border-[#2c3528]/20 text-[#2c3528] hover:bg-[#2c3528] hover:text-[#f9f6f0]"
          aria-label="Close menu"
        >
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
      </div>

      {/* Category chips */}
      <div className="px-6 md:px-10 py-4 border-b border-[#2c3528]/15 bg-[#f0ebe1] overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {visibleCategories.map((c) => (
            <button
              key={c.name}
              onClick={() => scrollToCategory(c.name)}
              data-testid={`dlg-cat-chip-${slug(c.name)}`}
              className={`shrink-0 px-3.5 py-2 text-[11px] md:text-xs tracking-[0.14em] uppercase border transition-colors ${
                active === c.name
                  ? "bg-[#5b7053] text-[#f9f6f0] border-[#5b7053]"
                  : "bg-transparent text-[#2c3528] border-[#2c3528]/25 hover:bg-[#2c3528] hover:text-[#f9f6f0] hover:border-[#2c3528]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable body */}
      <div
        id="full-menu-scroll"
        className="flex-1 overflow-y-auto px-6 md:px-10 py-10 space-y-16"
      >
        {visibleCategories.map((cat, ci) => (
          <section
            key={cat.name}
            id={"dlg-cat-" + slug(cat.name)}
            data-testid={`dlg-category-${ci}`}
            className="scroll-mt-6"
          >
            <div className="border-t border-[#2c3528]/20 pt-8">
              <p className="overline text-[#d98a6c]">
                {String(ci + 1).padStart(2, "0")} · Section
              </p>
              <h4 className="font-serif-display mt-2 text-3xl md:text-4xl text-[#2c3528] leading-tight">
                {cat.name}
              </h4>
              {cat.blurb ? (
                <p className="mt-2 text-[#2c3528]/70 italic font-serif-display text-lg max-w-2xl">
                  {cat.blurb}
                </p>
              ) : null}

              <ul className="mt-8 divide-y divide-[#2c3528]/15">
                {cat.items.map((it, i) => (
                  <li
                    key={it.name + i}
                    data-testid={`dlg-item-${slug(cat.name)}-${i}`}
                    className="py-4 md:py-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h5 className="font-serif-display text-xl md:text-2xl text-[#2c3528] leading-tight">
                        {it.name}
                      </h5>
                      {it.price ? (
                        <span
                          data-testid={`dlg-item-price-${slug(cat.name)}-${i}`}
                          className="shrink-0 text-sm md:text-base text-[#5b7053] font-medium"
                        >
                          {it.price}
                        </span>
                      ) : null}
                    </div>
                    {it.description ? (
                      <p className="mt-1.5 text-[#2c3528]/70 leading-relaxed text-sm md:text-base max-w-2xl">
                        {it.description}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}
        <div className="text-center text-[#2c3528]/55 italic font-serif-display text-lg pb-2">
          Καλή όρεξη · Enjoy your meal
        </div>
      </div>
    </>
  );
};

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
